import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Trash2, AlertTriangle, X, Loader2, Ban, CheckCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface DeleteConfirmationOptions {
  title?: string;
  itemName: string;
  itemType?: string;
  warningMessage?: string;
  confirmText?: string;
  confirmIcon?: LucideIcon;
  onConfirm: () => Promise<void>;
}

interface DeleteConfirmationContextType {
  confirmDelete: (options: DeleteConfirmationOptions) => Promise<boolean>;
}

const DeleteConfirmationContext = createContext<DeleteConfirmationContextType | null>(null);

// Generate random 4-digit PIN
const generatePin = (): string => {
  return Math.floor(1000 + Math.random() * 9000).toString();
};

interface ModalState {
  isOpen: boolean;
  options: DeleteConfirmationOptions | null;
  pin: string;
}

const DeleteConfirmationModal: React.FC<{
  state: ModalState;
  inputPin: string;
  isLoading: boolean;
  onInputChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ state, inputPin, isLoading, onInputChange, onConfirm, onCancel }) => {
  if (!state.isOpen || !state.options) return null;

  const isPinMatch = inputPin === state.pin;
  const canDelete = isPinMatch && !isLoading;
  const confirmText = state.options.confirmText || 'Eliminar';
  const ConfirmIcon = state.options.confirmIcon || Trash2;

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && !isLoading && onCancel()}
    >
      {/* Overlay - Glass Claro */}
      <div className="absolute inset-0 bg-white/30 backdrop-blur-md" />

      {/* Modal - Glass Claro */}
      <div className="relative bg-white/85 backdrop-blur-xl border border-white/50 shadow-2xl rounded-3xl w-full max-w-md overflow-hidden animate-in zoom-in-95 fade-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-xl">
              <AlertTriangle className="text-red-500" size={24} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">
              {state.options.title || `Confirmar ${confirmText}`}
            </h3>
          </div>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Item Info Box */}
          <div className="bg-slate-50/80 border border-slate-200/50 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <Trash2 className="text-red-400" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">{confirmText}:</p>
                <p className="font-bold text-slate-800 text-lg">{state.options.itemName}</p>
                {state.options.itemType && (
                  <p className="text-xs text-slate-400">Tipo: {state.options.itemType}</p>
                )}
              </div>
            </div>
          </div>

          {/* Warning Message */}
          <div className="flex items-start gap-2 text-red-600 bg-red-50/50 p-3 rounded-xl">
            <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium">
              {state.options.warningMessage || 'Esta accion no se puede deshacer.'}
            </p>
          </div>

          {/* PIN Section */}
          <div className="space-y-3">
            <p className="text-sm text-slate-600 text-center">
              Para confirmar, escribe el codigo:
            </p>

            {/* PIN Display */}
            <div className="bg-red-50/50 border-2 border-dashed border-red-200 rounded-xl py-4 px-6">
              <p className="text-3xl font-mono font-bold text-red-600 text-center tracking-[0.5em]">
                {state.pin}
              </p>
            </div>

            {/* PIN Input */}
            <div className="relative">
              <input
                type="text"
                maxLength={4}
                value={inputPin}
                onChange={(e) => onInputChange(e.target.value.replace(/\D/g, ''))}
                disabled={isLoading}
                placeholder="_ _ _ _"
                className={`
                  w-full text-center text-2xl font-mono font-bold tracking-[0.5em] py-3 px-4
                  border-2 rounded-xl transition-all duration-200
                  focus:outline-none focus:ring-4
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${isPinMatch
                    ? 'border-emerald-400 bg-emerald-50 text-emerald-600 focus:ring-emerald-100'
                    : 'border-slate-200 bg-white text-slate-800 focus:border-primary-400 focus:ring-primary-100'
                  }
                `}
                autoFocus
              />
              {isPinMatch && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 pt-2">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-3 text-slate-600 font-medium bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={!canDelete}
            className={`
              flex-1 px-4 py-3 font-bold rounded-xl transition-all duration-200
              flex items-center justify-center gap-2
              ${canDelete
                ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/25'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }
            `}
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {confirmText}...
              </>
            ) : (
              <>
                <ConfirmIcon size={18} />
                {confirmText}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export const DeleteConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    options: null,
    pin: '',
  });
  const [inputPin, setInputPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirmDelete = useCallback((options: DeleteConfirmationOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setModalState({
        isOpen: true,
        options,
        pin: generatePin(),
      });
      setInputPin('');
      setIsLoading(false);
    });
  }, []);

  const handleCancel = useCallback(() => {
    setModalState({ isOpen: false, options: null, pin: '' });
    setInputPin('');
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!modalState.options || inputPin !== modalState.pin) return;

    setIsLoading(true);
    try {
      await modalState.options.onConfirm();
      setModalState({ isOpen: false, options: null, pin: '' });
      setInputPin('');
      resolveRef.current?.(true);
    } catch (error) {
      // El error se maneja en onConfirm, solo cerramos el modal
      setModalState({ isOpen: false, options: null, pin: '' });
      setInputPin('');
      resolveRef.current?.(false);
    } finally {
      setIsLoading(false);
      resolveRef.current = null;
    }
  }, [modalState, inputPin]);

  return (
    <DeleteConfirmationContext.Provider value={{ confirmDelete }}>
      {children}
      <DeleteConfirmationModal
        state={modalState}
        inputPin={inputPin}
        isLoading={isLoading}
        onInputChange={setInputPin}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </DeleteConfirmationContext.Provider>
  );
};

export const useDeleteConfirmation = (): DeleteConfirmationContextType => {
  const context = useContext(DeleteConfirmationContext);
  if (!context) {
    throw new Error('useDeleteConfirmation must be used within a DeleteConfirmationProvider');
  }
  return context;
};
